import React, { HTMLAttributes, useCallback, useEffect, useRef, useState } from "react"

import { IconCopy } from "@tabler/icons-react"

import { cn } from "@/lib/utils"

import { Button, ButtonProps } from "./cn/button"
import { Tooltip, TooltipProps } from "./cn/tooltip"

function copyWithLegacyClipboard(text: string): boolean {
  const textarea = document.createElement("textarea")
  const activeElement = document.activeElement
  textarea.value = text
  textarea.setAttribute("readonly", "")
  textarea.style.cssText = "position: fixed; opacity: 0"
  document.body.append(textarea)
  textarea.select()
  textarea.setSelectionRange(0, text.length)
  const copied = document.execCommand("copy")
  textarea.remove()
  if (activeElement instanceof HTMLElement) activeElement.focus()
  return copied
}

export async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text)
  } catch {
    // The async API is blocked in some embedded app views despite a click
    // gesture. execCommand remains available there and still requires the click.
    if (!copyWithLegacyClipboard(text)) throw new Error("Clipboard write failed")
  }
}

export interface CopyButtonProps {
  className?: string
  text: string
  tooltip?: string
  icon?: ButtonProps["icon"]
  size?: ButtonProps["size"]
  position?: "left" | "right"
  tooltipSide?: TooltipProps["side"]
}

export interface CopyToClipboardProps extends Omit<
  HTMLAttributes<HTMLSpanElement>,
  "children"
> {
  text: string
  children?: React.ReactNode
  size?: ButtonProps["size"]
  buttonPosition?: "left" | "right"
}

export const CopyToClipboard = ({
  text,
  children,
  size = "small",
  buttonPosition = "right",
  ...props
}: CopyToClipboardProps) => {
  return (
    <span {...props} className="inline-flex gap-1 align-middle">
      {buttonPosition === "left" && (
        <CopyButton text={text} size={size} position={buttonPosition} />
      )}
      {children || text}
      {buttonPosition === "right" && (
        <CopyButton text={text} size={size} position={buttonPosition} />
      )}
    </span>
  )
}

export const CopyButton = ({
  className,
  text,
  tooltip,
  icon = IconCopy,
  size = "small",
  tooltipSide = "top",
}: CopyButtonProps) => {
  // Names the button as well as the tooltip: it renders icon-only, so without
  // this it reaches a screen reader with no name at all. Deliberately the hint
  // rather than the "Copied!" confirmation, so the name stays put mid-press.
  const hint = tooltip || "Copy to clipboard"
  const [copied, setCopied] = useState(false)
  const [copyFailed, setCopyFailed] = useState(false)
  const [open, setOpen] = useState(false)
  // Prevent the tooltip's close-on-press from hiding the success confirmation.
  const confirming = useRef(false)
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current)
    },
    [],
  )

  const handleCopy = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault()
      void copyText(text).then(
        () => {
          confirming.current = true
          setCopied(true)
          setCopyFailed(false)
          setOpen(true)
          if (resetTimer.current) clearTimeout(resetTimer.current)
          resetTimer.current = setTimeout(() => {
            // Only start closing. The label stays "Copied!" so it doesn't flip back
            // to the hint mid-fade; it resets in onOpenChangeComplete once gone.
            confirming.current = false
            setOpen(false)
          }, 1500)
        },
        () => {
          setCopied(false)
          setCopyFailed(true)
          setOpen(true)
        },
      )
    },
    [text],
  )

  return (
    <Tooltip
      open={open}
      onOpenChange={(next) => {
        // Hold the "Copied!" confirmation open through the click that triggered
        // it; otherwise mirror the tooltip's hover/focus open state.
        if (!next && confirming.current) return
        // A genuine (post-confirmation) reopen goes back to the hint.
        if (next && !confirming.current) {
          setCopied(false)
          setCopyFailed(false)
        }
        setOpen(next)
      }}
      // Reset the label only after the popup has fully closed, so "Copied!"
      // survives the exit animation instead of swapping mid-fade.
      onOpenChangeComplete={(isOpen) => {
        if (!isOpen) {
          setCopied(false)
          setCopyFailed(false)
        }
      }}
      content={copied ? "Copied!" : copyFailed ? "Couldn't copy" : hint}
      side={tooltipSide}
    >
      <Button
        className={cn("-my-1 inline align-middle", className)}
        variant="neutral"
        size={size}
        noBorder
        iconOnly
        icon={icon}
        aria-label={hint}
        onClick={handleCopy}
      />
    </Tooltip>
  )
}
